import { useAccount } from "@qw/auth";
import type { InboxConversationsListConversationFragment } from "@qw/graphql";
import { getDisplayLabels, KnownInboxLabel } from "@qw/qw-common";
import {
  Badge,
  cn,
  FilePreviewDialog,
  Icon,
  type PartialFileUpload,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useFilePreviewDialog,
} from "@qw/ui";
import { type FC, type FocusEvent, Fragment, type KeyboardEvent, useMemo, useState } from "react";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { type ConversationMenuItemId, useCoversationMenu } from "@/providers/use-conversation-menu";
import { getQuoteFollowUpWakeUpTitle } from "@/utils/quote-follow-up-wake-up";
import { ConversationLinkActions } from "./conversation-link-actions";
import { ConversationLinkButton } from "./conversation-link-button";
import { ConversationLinkCheckbox } from "./conversation-link-checkbox";
import {
  type AttachmentPreview,
  ConversationLinkContent,
  type ConversationLinkContentProps,
  getAttachmentPreviewMimeType,
} from "./conversation-link-content";
import { ConversationLinkMenu } from "./conversation-link-menu";
import { getConversationLinkSender } from "./conversation-link-sender";

interface ConversationLinkProps {
  conversation: InboxConversationsListConversationFragment;
}

const QUICK_ACTION_IDS = new Set<ConversationMenuItemId>(["reply-all", "forward", "star", "inbox", "read-state"]);
const NO_FILE_PREVIEW_NAV_FILES: PartialFileUpload[] = [];

export const ConversationLink: FC<ConversationLinkProps> = ({ conversation }) => {
  const { userInbox } = useAccount();
  const { conversationId, setConversationId } = useInboxQueryStates();
  const [areActionsKeyboardActive, setAreActionsKeyboardActive] = useState(false);
  const { menuItems } = useCoversationMenu({ conversation });

  const {
    id,
    attachmentCount,
    attachmentPreviews,
    draftCount,
    dueWakeUp,
    inboxId,
    insuredId,
    internalParticipantEmails,
    labels,
    lastMessageAt,
    lastMessageFrom,
    messageCount,
    participants,
    subject,
    isPending,
    viewerIsUnread,
  } = conversation;

  // A conversation whose inbox is not the viewer's own is surfaced because they collaborate on the
  // linked marketing plan — mark it so it's clear this is a shared deal, not their own mailbox.
  const isCollaboratedDeal = Boolean(userInbox && inboxId !== userInbox.id);
  const isCurrent = id === conversationId;
  const hasSelectedConversation = Boolean(conversationId);
  // A collaborated deal shows the owner's copy, so its read state is the viewer's own receipt
  // (`viewerIsUnread`), not the owner's `UNREAD` label. Own-inbox rows keep the label — it's what the
  // optimistic label mutations flip.
  const isUnread = useMemo(
    () => (isCollaboratedDeal ? viewerIsUnread : labels.includes(KnownInboxLabel.UNREAD)),
    [isCollaboratedDeal, viewerIsUnread, labels]
  );
  const isStarred = useMemo(() => labels.includes(KnownInboxLabel.STARRED), [labels]);
  const displayLabels = useMemo(() => getDisplayLabels(labels), [labels]);
  const filePreview = useFilePreviewDialog({ files: NO_FILE_PREVIEW_NAV_FILES });
  const hasQuickActions = useMemo(
    () => menuItems.some((item) => item.type === "button" && QUICK_ACTION_IDS.has(item.id)),
    [menuItems]
  );
  const canShowQuickActions = !hasSelectedConversation && hasQuickActions;
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocusTarget = event.relatedTarget as Node | null;

    if (nextFocusTarget && event.currentTarget.contains(nextFocusTarget)) {
      return;
    }

    setAreActionsKeyboardActive(false);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      !canShowQuickActions ||
      event.key !== "ArrowRight" ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.target !== event.currentTarget
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setAreActionsKeyboardActive(true);

    const rowElement = event.currentTarget;

    requestAnimationFrame(() => {
      rowElement.querySelector<HTMLButtonElement>("[data-conversation-link-action]:not(:disabled)")?.focus();
    });
  };

  const sender = useMemo(
    () =>
      getConversationLinkSender({
        currentUserEmail: userInbox?.email,
        internalInboxEmails: internalParticipantEmails ?? [],
        lastMessageFrom,
        participants,
      }),
    [internalParticipantEmails, lastMessageFrom, participants, userInbox?.email]
  );

  return (
    <>
      <div>
        <ConversationLinkMenu menuItems={menuItems}>
          <ConversationLinkButton
            onBlur={handleBlur}
            onClick={() => setConversationId(id)}
            onKeyDown={handleKeyDown}
            aria-keyshortcuts={canShowQuickActions ? "ArrowRight" : undefined}
            isCurrent={isCurrent}
            isUnread={isUnread}
          >
            <ConversationLinkContentWithAttachmentPreview
              icon={<ConversationLinkCheckbox row={conversation} isCurrent={isCurrent} />}
              from={<ConversationLinkSender sender={sender} isUnread={isUnread} />}
              date={lastMessageAt}
              subject={subject}
              isUnread={isUnread}
              isStarred={isStarred}
              isCurrent={isCurrent}
              insuredId={insuredId}
              badges={
                <ConversationLinkBadges
                  isPending={isPending}
                  isCollaborated={isCollaboratedDeal}
                  dueWakeUp={dueWakeUp}
                  isCurrent={isCurrent}
                />
              }
              attachmentCount={attachmentCount}
              attachmentPreviews={attachmentPreviews}
              compactLabels={Boolean(conversationId)}
              displayLabels={displayLabels}
              draftCount={draftCount}
              messageCount={messageCount}
              onPreviewFileOpen={(file) => filePreview.openFile(file)}
            />
            {canShowQuickActions && (
              <ConversationLinkActions isKeyboardActive={areActionsKeyboardActive} menuItems={menuItems} />
            )}
          </ConversationLinkButton>
        </ConversationLinkMenu>
      </div>
      <FilePreviewDialog {...filePreview.dialogProps} />
    </>
  );
};

const getAttachmentPreviewFile = (attachment: AttachmentPreview): PartialFileUpload => ({
  id: attachment.fileUploadId,
  filename: attachment.filename,
  mimeType: getAttachmentPreviewMimeType(attachment),
});

const ConversationLinkContentWithAttachmentPreview: FC<
  ConversationLinkContentProps & { onPreviewFileOpen: (file: PartialFileUpload) => void }
> = ({ onPreviewFileOpen, ...props }) => {
  const handleAttachmentPreviewOpen = (attachment: AttachmentPreview) => {
    onPreviewFileOpen(getAttachmentPreviewFile(attachment));
  };

  return <ConversationLinkContent {...props} onAttachmentPreviewOpen={handleAttachmentPreviewOpen} />;
};

export const ConversationLinkBadges: FC<{
  isPending: boolean;
  isCollaborated?: boolean;
  dueWakeUp?: InboxConversationsListConversationFragment["dueWakeUp"] | null;
  isCurrent: boolean;
}> = ({ isPending, isCollaborated, dueWakeUp, isCurrent }) => {
  const quoteFollowUpTitle =
    dueWakeUp?.sourceReason === "QuoteFollowUp" ? getQuoteFollowUpWakeUpTitle(dueWakeUp) : null;

  return (
    <>
      {isCollaborated && (
        <Tooltip delayDuration={500}>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Badge
                variant="secondary"
                className={cn("gap-1", isCurrent && "bg-background/15 text-primary-foreground")}
              >
                <Icon icon="group" className="text-xs" /> Collaborating
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent>A deal you collaborate on — this thread lives in the plan owner's inbox.</TooltipContent>
        </Tooltip>
      )}
      {isPending && (
        <Badge variant="secondary" className={cn("gap-1", isCurrent && "bg-background/15 text-primary-foreground")}>
          <Icon icon="search_activity" className="text-xs" /> Processing
        </Badge>
      )}
      {quoteFollowUpTitle && (
        <Tooltip delayDuration={500}>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Badge
                variant="secondary"
                className={cn("gap-1", isCurrent && "bg-background/15 text-primary-foreground")}
              >
                <Icon icon="notifications_active" className="text-xs" /> Follow up
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent>{quoteFollowUpTitle}</TooltipContent>
        </Tooltip>
      )}
    </>
  );
};

const ConversationLinkSender: FC<{ sender: ReturnType<typeof getConversationLinkSender>; isUnread: boolean }> = ({
  sender,
  isUnread,
}) => (
  <>
    {sender.participants.map((participant, index) => (
      <Fragment key={participant.email}>
        {index > 0 && <span className="font-normal opacity-70">, </span>}
        <span
          className={cn(
            participant.isPrimary ? (isUnread ? "font-semibold" : "font-medium") : "font-normal opacity-70"
          )}
        >
          {participant.fullName || participant.email}
        </span>
      </Fragment>
    ))}
  </>
);
